import {
  DesignElementXAnchor,
  DesignElementYAnchor,
  PlacedDesignElement,
} from '../../../api/tendApi';

/**
 * Compute the element's center position in pixels on a canvas of the given
 * dimensions. Handles both anchored placements and legacy ones.
 */
export function placedElementCenterPx(
  p: PlacedDesignElement,
  canvasW: number,
  canvasH: number,
): { centerX: number; centerY: number } {
  if (!p.xAnchor || !p.yAnchor) {
    // Legacy: xPct = % of width, yPct = % of HEIGHT, both → center
    return {
      centerX: (p.xPct / 100) * canvasW,
      centerY: (p.yPct / 100) * canvasH,
    };
  }

  // Anchored: offsets are % of WIDTH for both axes.
  let centerX: number;
  if (p.xAnchor === 'left') centerX = (p.xPct / 100) * canvasW;
  else if (p.xAnchor === 'right') centerX = canvasW - (p.xPct / 100) * canvasW;
  else centerX = canvasW / 2 + (p.xPct / 100) * canvasW;

  let centerY: number;
  if (p.yAnchor === 'top') centerY = (p.yPct / 100) * canvasW;
  else if (p.yAnchor === 'bottom') centerY = canvasH - (p.yPct / 100) * canvasW;
  else centerY = canvasH / 2 + (p.yPct / 100) * canvasW;

  return { centerX, centerY };
}

/**
 * Inverse of placedElementCenterPx: given an absolute center position on the
 * canvas, derive an auto-anchored placement (3x3 grid). The cell the center
 * falls into determines the anchor; the offset is stored as % of canvas WIDTH
 * relative to that anchor.
 */
export function deriveAnchoredOffsets(
  centerX: number,
  centerY: number,
  canvasW: number,
  canvasH: number,
): {
  xAnchor: DesignElementXAnchor;
  yAnchor: DesignElementYAnchor;
  xPct: number;
  yPct: number;
} {
  const xPctOfWidth = (centerX / canvasW) * 100;
  const yPctOfHeight = canvasH > 0 ? (centerY / canvasH) * 100 : 0;

  const xAnchor: DesignElementXAnchor =
    xPctOfWidth < 33.333 ? 'left' : xPctOfWidth < 66.666 ? 'center' : 'right';
  const yAnchor: DesignElementYAnchor =
    yPctOfHeight < 33.333 ? 'top' : yPctOfHeight < 66.666 ? 'middle' : 'bottom';

  let xPct: number;
  if (xAnchor === 'left') xPct = (centerX / canvasW) * 100;
  else if (xAnchor === 'right') xPct = ((canvasW - centerX) / canvasW) * 100;
  else xPct = ((centerX - canvasW / 2) / canvasW) * 100;

  let yPct: number;
  if (yAnchor === 'top') yPct = (centerY / canvasW) * 100;
  else if (yAnchor === 'bottom') yPct = ((canvasH - centerY) / canvasW) * 100;
  else yPct = ((centerY - canvasH / 2) / canvasW) * 100;

  return { xAnchor, yAnchor, xPct, yPct };
}

/**
 * Clamp placement so the element doesn't drift out of the canvas:
 *   - SIZE: widthPct can't exceed canvas width and (after deriving height
 *     from aspectRatio) can't exceed canvas height. This gives natural
 *     "snap to full" behavior on non-rotated drags.
 *   - POSITION (non-rotated): full unrotated bounding box stays inside, so
 *     rectangular bars sit flush with edges as you push them.
 *   - POSITION (rotated): only the center is constrained to stay inside the
 *     canvas. Rotated corners are allowed to poke past any edge symmetrically.
 *
 * Returns an always-anchored placement (re-derives the 3x3 anchor from the
 * clamped center).
 */
const MIN_WIDTH_PCT = 2;
export function clampPlacement(
  p: PlacedDesignElement,
  canvasW: number,
  canvasH: number,
): PlacedDesignElement {
  if (canvasW <= 0 || canvasH <= 0) return p;
  const ar = p.aspectRatio || 1;

  // Size cap: largest unrotated width that fits inside the canvas in both axes.
  const maxWidthPx = Math.min(canvasW, canvasH * ar);
  const maxWidthPct = (maxWidthPx / canvasW) * 100;
  const widthPct = Math.max(MIN_WIDTH_PCT, Math.min(maxWidthPct, p.widthPct));

  const widthPx = (widthPct / 100) * canvasW;
  const heightPx = widthPx / ar;

  // Position cap: tight (unrotated bbox) when rotation=0; loose (center-only)
  // when rotated, so corners can poke out symmetrically on all four sides.
  const isRotated = ((p.rotation || 0) % 360) !== 0;
  const halfW = isRotated ? 0 : widthPx / 2;
  const halfH = isRotated ? 0 : heightPx / 2;

  const { centerX, centerY } = placedElementCenterPx(
    { ...p, widthPct },
    canvasW,
    canvasH,
  );
  const clampedCx = Math.max(halfW, Math.min(canvasW - halfW, centerX));
  const clampedCy = Math.max(halfH, Math.min(canvasH - halfH, centerY));

  const anchored = deriveAnchoredOffsets(clampedCx, clampedCy, canvasW, canvasH);
  return { ...p, widthPct, ...anchored };
}

/**
 * Build a CSS style block (left/top/width as px, transform centering) for an
 * <img> rendering this placement on a canvas of the given dimensions.
 *
 * Width is derived from widthPct of canvasW. Height auto from aspectRatio.
 * Position is the element's CENTER → translate(-50%,-50%) is applied.
 */
export function getPlacedElementStyle(
  p: PlacedDesignElement,
  canvasW: number,
  canvasH: number,
): React.CSSProperties {
  const { centerX, centerY } = placedElementCenterPx(p, canvasW, canvasH);
  const widthPx = (p.widthPct / 100) * canvasW;
  const ar = p.aspectRatio || 1;
  const heightPx = widthPx / ar;
  return {
    position: 'absolute',
    left: `${centerX}px`,
    top: `${centerY}px`,
    width: `${widthPx}px`,
    height: `${heightPx}px`,
    transform: `translate(-50%, -50%) rotate(${p.rotation}deg)`,
    transformOrigin: 'center center',
  };
}
